import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useMainCompany } from "../../context/MainCompanyContext";
import api from "../../api/axios";
import AdminPageHeader from "../../components/common/AdminPageHeader";
import Modal from "../../components/common/AdminModal";
import { Pencil, Trash, PlusCircle, RefreshCw, FilePlus } from "lucide-react";
import { Editor } from '@tinymce/tinymce-react';

// Debounce hook (copied from your users page — small and useful)
function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// Helper to make slugs (useful for SEO / canonical)
function makeSlug(text = "") {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-");
}

export default function AdminManageCMS() {
  const { selectedMain } = useMainCompany();

  const TABS = [
    { id: "pages", label: "Pages" },
    { id: "faqs", label: "FAQs" },
    { id: "updates", label: "Latest Updates / Features" },
  ];

  const CORE_PAGE_KEYS = [
    { key: "about_us", label: "About Us" },
    { key: "contact_us", label: "Contact Us" },
    { key: "introduction", label: "Introduction" },
    { key: "privacy_policy", label: "Privacy Policy" },
    { key: "terms_of_use", label: "Terms of Use" },
  ];

  const [tab, setTab] = useState("pages");

  // Pages (single-row pages like About Us, Contact Us)
  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);

  // FAQs
  const [faqs, setFaqs] = useState([]);
  const [faqsLoading, setFaqsLoading] = useState(false);

  // Updates
  const [updates, setUpdates] = useState([]);
  const [updatesLoading, setUpdatesLoading] = useState(false);

  // Common UI state
  const [modal, setModal] = useState({ type: null, data: null });
  // helper to show informational modal instead of alert()
  const showInfo = (message) => setModal({ type: "info", data: { message } });
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 250);

  // paging (used for faqs & updates lists)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // fetch helpers
  // fetch helpers
  const fetchPages = useCallback(async () => {
    if (!selectedMain) return;
    setPagesLoading(true);
    try {
      const { data } = await api.get("/admin/cms/pages", {
        params: { maincompanyid: selectedMain },
      });
      // Convert status to string for consistency since it changed from text to smallint
      const pagesData = Array.isArray(data?.data) ? data.data.map(page => ({
        ...page,
        status: String(page.status)
      })) : [];
      setPages(pagesData);
    } catch (err) {
      console.error("fetchPages error:", err);
    } finally {
      setPagesLoading(false);
    }
  }, [selectedMain]);

  const fetchFaqs = useCallback(async () => {
    if (!selectedMain) return;
    setFaqsLoading(true);
    try {
      const { data } = await api.get("/admin/cms/faqs", {
        params: { maincompanyid: selectedMain },
      });
      // Convert status to string for consistency since it changed from text to smallint
      const faqsData = Array.isArray(data?.data) ? data.data.map(faq => ({
        ...faq,
        status: String(faq.status)
      })) : [];
      setFaqs(faqsData);
    } catch (err) {
      console.error("fetchFaqs error:", err);
    } finally {
      setFaqsLoading(false);
    }
  }, [selectedMain]);

  const fetchUpdates = useCallback(async () => {
    if (!selectedMain) return;
    setUpdatesLoading(true);
    try {
      const { data } = await api.get("/admin/cms/updates", {
        params: { maincompanyid: selectedMain },
      });
      // Convert status to string for consistency since it changed from text to smallint
      const updatesData = Array.isArray(data?.data) ? data.data.map(update => ({
        ...update,
        status: String(update.status)
      })) : [];
      setUpdates(updatesData);
    } catch (err) {
      console.error("fetchUpdates error:", err);
    } finally {
      setUpdatesLoading(false);
    }
  }, [selectedMain]);


  // On company change -> fetch all
  useEffect(() => {
    if (!selectedMain) return;
    fetchPages();
    fetchFaqs();
    fetchUpdates();
  }, [selectedMain, fetchPages, fetchFaqs, fetchUpdates]);

  // --- Pages Edit flow ---
  // Open edit modal: pass page object (or an object with page_name to create default)
  const openEditPage = (pageObj) => {
    setModal({ type: "editPage", data: { ...pageObj } });
  };

  const handlePageSave = async (payload) => {
    try {
      // Convert status to number for API since it changed to smallint
      const apiPayload = { ...payload, status: parseInt(payload.status, 10) };
      if (payload.id) {
        await api.put(`/admin/cms/pages/${payload.id}`, apiPayload);
      } else {
        await api.post(`/admin/cms/pages`, apiPayload);
      }
      await fetchPages();
      setModal({ type: null });
    } catch (err) {
      console.error("handlePageSave error:", err);
      showInfo(err.response?.data?.message || "Failed to save page");
    }
  };

  // --- FAQ flow ---
  const openAddFaq = () => setModal({ type: "addFaq", data: {} });
  const openEditFaq = (faq) => setModal({ type: "editFaq", data: { ...faq } });
  const confirmDeleteFAQ = (faq) => setModal({ type: "deleteFaq", data: faq });

  const handleSaveFaq = async (payload) => {
    try {
      // Convert status to number for API since it changed to smallint
      const apiPayload = { ...payload, status: parseInt(payload.status, 10) };
      if (payload.id) {
        await api.put(`/admin/cms/faqs/${payload.id}`, apiPayload);
      } else {
        await api.post(`/admin/cms/faqs`, apiPayload);
      }
      await fetchFaqs();
      setModal({ type: null });
    } catch (err) {
      console.error("handleSaveFaq error:", err);
      showInfo(err.response?.data?.message || "Failed to save FAQ");
    }
  };

  const handleDeleteFaq = async (id) => {
    try {
      await api.delete(`/admin/cms/faqs/${id}`);
      await fetchFaqs();
      setModal({ type: null });
    } catch (err) {
      console.error("delete faq error:", err);
      showInfo("Failed to delete FAQ");
    }
  };

  const toggleFaqStatus = async (faq) => {
    try {
      // Convert status to number for API since it changed to smallint
      const newStatus = faq.status === "1" ? 0 : 1;
      await api.put(`/admin/cms/faqs/${faq.id}`, { ...faq, status: newStatus });
      await fetchFaqs();
    } catch (err) {
      console.error("toggleFaqStatus error:", err);
    }
  };

  // --- Updates flow ---
  const openAddUpdate = () => setModal({ type: "addUpdate", data: {} });
  const openEditUpdate = (u) => setModal({ type: "editUpdate", data: { ...u } });
  const confirmDeleteUpdate = (u) => setModal({ type: "deleteUpdate", data: u });

  const handleSaveUpdate = async (payload) => {
    try {
      // Convert status to number for API since it changed to smallint
      const apiPayload = { ...payload, status: parseInt(payload.status, 10) };
      if (payload.id) {
        await api.put(`/admin/cms/updates/${payload.id}`, apiPayload);
      } else {
        await api.post(`/admin/cms/updates`, apiPayload);
      }
      await fetchUpdates();
      setModal({ type: null });
    } catch (err) {
      console.error("handleSaveUpdate error:", err);
      showInfo(err.response?.data?.message || "Failed to save update");
    }
  };

  const handleDeleteUpdate = async (id) => {
    try {
      await api.delete(`/admin/cms/updates/${id}`);
      await fetchUpdates();
      setModal({ type: null });
    } catch (err) {
      console.error("delete update error:", err);
      showInfo("Failed to delete update");
    }
  };

  const toggleUpdateStatus = async (u) => {
    try {
      // Convert status to number for API since it changed to smallint
      const newStatus = u.status === "1" ? 0 : 1;
      await api.put(`/admin/cms/updates/${u.id}`, { ...u, status: newStatus });
      await fetchUpdates();
    } catch (err) {
      console.error("toggleUpdateStatus error:", err);
    }
  };

  // Delete a page (used by PageEditModal)
  const handleDeletePage = async (id) => {
    try {
      if (!id) return;
      await api.delete(`/admin/cms/pages/${id}`);
      await fetchPages();
      setModal({ type: null });
    } catch (err) {
      console.error('delete page error:', err);
      showInfo('Failed to delete page');
    }
  };

  // Simple file upload helper (returns filename)
  const uploadImage = async (file) => {
    if (!file) return null;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data?.filename || null;
    } catch (err) {
      console.error("uploadImage error:", err);
      showInfo("Image upload failed");
      return null;
    }
  };

  // ----- filtered lists (search) -----
  const filteredFaqs = useMemo(() => {
    if (!debouncedSearch) return faqs;
    return faqs.filter((f) => (f.question || "").toLowerCase().includes(debouncedSearch.toLowerCase()) || (f.answer || "").toLowerCase().includes(debouncedSearch.toLowerCase()));
  }, [faqs, debouncedSearch]);

  const filteredUpdates = useMemo(() => {
    if (!debouncedSearch) return updates;
    return updates.filter((u) => (u.title || "").toLowerCase().includes(debouncedSearch.toLowerCase()) || (u.summary || "").toLowerCase().includes(debouncedSearch.toLowerCase()));
  }, [updates, debouncedSearch]);

  // paging helpers
  const totalFaqPages = Math.max(1, Math.ceil(filteredFaqs.length / pageSize));
  const totalUpdatePages = Math.max(1, Math.ceil(filteredUpdates.length / pageSize));

  const paginatedFaqs = filteredFaqs.slice((page - 1) * pageSize, page * pageSize);
  const paginatedUpdates = filteredUpdates.slice((page - 1) * pageSize, page * pageSize);

  // UI pieces for the header actions
  const headerActions = (
    <>
      {tab === "faqs" && (
        <button onClick={openAddFaq} className="flex items-center gap-1 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700">
          <PlusCircle className="w-4 h-4" /> Add FAQ
        </button>
      )}

      {tab === "updates" && (
        <button onClick={openAddUpdate} className="flex items-center gap-1 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700">
          <FilePlus className="w-4 h-4" /> Add Update
        </button>
      )}
    </>
  );

  // If no company is selected — show message
  if (!selectedMain) {
    return (
      <div className="p-4">
        <h3 className="text-lg font-medium">Manage CMS</h3>
        <div className="mt-4 p-4 bg-yellow-50 border rounded">Please select a company to manage CMS content.</div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <AdminPageHeader
        title="Manage CMS"
        actions={headerActions}
        filters={(
          <div className="flex items-center gap-2">
            <input type="text" placeholder="Search..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="p-2 border rounded text-sm w-64" />
            <button onClick={() => { setSearch(""); setPage(1); }} className="px-3 py-1 border rounded text-sm"> <RefreshCw className="w-4 h-4 inline" /> Reset</button>
          </div>
        )}
      />

      {/* Tabs */}
      <div className="mt-4">
        <div className="flex gap-2">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-1 rounded ${tab === t.id ? "bg-blue-600 text-white" : "bg-gray-100"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-4">
          {tab === "pages" && (
            <div>
              {pagesLoading ? (
                <div className="p-4">Loading pages...</div>
              ) : (
                <div className="grid gap-3">
                  {CORE_PAGE_KEYS.map((k) => {
                    const row = pages.find((p) => (p.page_name || '').trim() === (k.key || '').trim()) || { page_name: k.key, value: "", keywords: "", meta_teg: "", meta_desc: "", intro_image: "", background_image: "", status: "0" };
                    return (
                      <div key={k.key} className="border rounded p-3 flex justify-between items-start gap-3">
                        <div>
                          <div className="text-sm font-medium">{k.label}</div>
                          <div className="text-xs text-gray-600 mt-1 line-clamp-3" dangerouslySetInnerHTML={{ __html: row.value || "<em>Empty</em>" }} />
                        </div>

                        <div className="flex gap-2 items-center">
                          <button title="Edit" onClick={() => openEditPage(row)} className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1">
                            <Pencil className="w-3 h-3" /> Edit</button>
                          <div className={`text-xs px-2 py-1 rounded ${row.status === "1" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{row.status === "1" ? "Active" : "Inactive"}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === "faqs" && (
            <div>
              {faqsLoading ? (
                <div>Loading FAQs...</div>
              ) : (
                <div>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 text-gray-700">
                        <tr>
                          <th className="px-2 py-2 text-left">Question</th>
                          <th className="px-2 py-2 text-left">Answer</th>
                          <th className="px-2 py-2 text-left">Status</th>
                          <th className="px-2 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedFaqs.map((f) => (
                          <tr key={f.id} className="odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                            <td className="px-2 py-2">{f.question}</td>
                            <td className="px-2 py-2">{f.value}</td>
                            <td className="px-2 py-2">{f.status === "1" ? "Active" : "Inactive"}</td>
                            <td className="px-2 py-2 text-right">
                              <div className="flex justify-end gap-2">
                                <button title="Toggle" onClick={() => toggleFaqStatus(f)} className="px-3 py-1 border rounded text-xs">{f.status === "1" ? "Disable" : "Enable"}</button>
                                <button title="Edit" onClick={() => openEditFaq(f)} className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                                <button title="Delete" onClick={() => confirmDeleteFAQ(f)} className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-1"><Trash className="w-3 h-3" /> Delete</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination controls */}
                  <div className="flex items-center justify-between gap-3 mt-3 text-sm">
                    <div>Showing {filteredFaqs.length ? (page - 1) * pageSize + 1 : 0}–{Math.min(page * pageSize, filteredFaqs.length)} of {filteredFaqs.length}</div>
                    <div className="flex gap-2 items-center">
                      <label className="text-gray-600">Rows</label>
                      <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="border rounded px-2 py-1">
                        {[5, 10, 25].map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                      <div className="flex gap-1">
                        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 border rounded">Prev</button>
                        <button onClick={() => setPage((p) => Math.min(totalFaqPages, p + 1))} disabled={page === totalFaqPages} className="px-3 py-1 border rounded">Next</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "updates" && (
            <div>
              {updatesLoading ? (
                <div>Loading Updates...</div>
              ) : (
                <div>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 text-gray-700">
                        <tr>
                          <th className="px-2 py-2 text-left">Title</th>
                          <th className="px-2 py-2 text-left">Summary</th>
                          <th className="px-2 py-2 text-left">Content</th>
                          <th className="px-2 py-2 text-left">Status</th>
                          <th className="px-2 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedUpdates.map((u) => (
                          <tr key={u.id} className="odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                            <td className="px-2 py-2">{u.question}</td>
                            <td className="px-2 py-2">{u.keywords}</td>
                            <td className="px-2 py-2">{u.value}</td>
                            <td className="px-2 py-2">{u.status === "1" ? "Active" : "Inactive"}</td>
                            <td className="px-2 py-2 text-right">
                              <div className="flex justify-end gap-2">
                                <button title="Toggle" onClick={() => toggleUpdateStatus(u)} className="px-3 py-1 border rounded text-xs">{u.status === "1" ? "Disable" : "Enable"}</button>
                                <button title="Edit" onClick={() => openEditUpdate(u)} className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                                <button title="Delete" onClick={() => confirmDeleteUpdate(u)} className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-1"><Trash className="w-3 h-3" /> Delete</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination controls */}
                  <div className="flex items-center justify-between gap-3 mt-3 text-sm">
                    <div>Showing {filteredUpdates.length ? (page - 1) * pageSize + 1 : 0}–{Math.min(page * pageSize, filteredUpdates.length)} of {filteredUpdates.length}</div>
                    <div className="flex gap-2 items-center">
                      <label className="text-gray-600">Rows</label>
                      <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="border rounded px-2 py-1">
                        {[5, 10, 25].map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                      <div className="flex gap-1">
                        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 border rounded">Prev</button>
                        <button onClick={() => setPage((p) => Math.min(totalUpdatePages, p + 1))} disabled={page === totalUpdatePages} className="px-3 py-1 border rounded">Next</button>
                      </div>
                    </div>
                  </div>

                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* -------- Modals for add/edit pages, faqs, updates, confirm delete -------- */}
      {modal.type === "editPage" && (
        <PageEditModal
          data={modal.data}
          onClose={() => setModal({ type: null })}
          onSave={async (payload) => {
            // ensure maincompanyid and slug
            payload.maincompanyid = selectedMain;
            payload.slug = payload.slug || makeSlug(payload.page_name || payload.title || "");
            // upload any files first if 'intro_file' or 'background_file' present (caller passes files)
            if (payload.intro_file) {
              const fn = await uploadImage(payload.intro_file);
              if (fn) payload.intro_image = fn;
            }
            if (payload.background_file) {
              const fn = await uploadImage(payload.background_file);
              if (fn) payload.background_image = fn;
            }
            await handlePageSave(payload);
          }}
          onDelete={async (id) => {
            await handleDeletePage(id);
          }}
        />
      )}

      {modal.type === "addFaq" && (
        <FaqEditModal
          data={{}}
          onClose={() => setModal({ type: null })}
          onSave={async (payload) => {
            payload.maincompanyid = selectedMain;
            await handleSaveFaq(payload);
          }}
        />
      )}

      {modal.type === "editFaq" && (
        <FaqEditModal
          data={modal.data}
          onClose={() => setModal({ type: null })}
          onSave={async (payload) => {
            payload.maincompanyid = selectedMain;
            await handleSaveFaq(payload);
          }}
        />
      )}

      {modal.type === "deleteFaq" && (
        <Modal title="Delete FAQ" onClose={() => setModal({ type: null })}>
          <p>Are you sure you want to delete this FAQ? This cannot be undone.</p>
          <div className="mt-4 flex justify-end gap-2">
            <button className="px-3 py-1 border rounded" onClick={() => setModal({ type: null })}>Cancel</button>
            <button className="px-3 py-1 bg-red-600 text-white rounded" onClick={() => handleDeleteFaq(modal.data.id)}>Yes, Delete</button>
          </div>
        </Modal>
      )}

      {modal.type === "info" && (
        <Modal title="Info" onClose={() => setModal({ type: null })}>
          <div className="text-sm">{modal.data?.message}</div>
          <div className="mt-4 flex justify-end">
            <button className="px-3 py-1 bg-blue-600 text-white rounded" onClick={() => setModal({ type: null })}>OK</button>
          </div>
        </Modal>
      )}

      {modal.type === "addUpdate" && (
        <UpdateEditModal data={{}} onClose={() => setModal({ type: null })} onSave={async (payload) => { payload.maincompanyid = selectedMain; await handleSaveUpdate(payload); }} />
      )}

      {modal.type === "editUpdate" && (
        <UpdateEditModal data={modal.data} onClose={() => setModal({ type: null })} onSave={async (payload) => { payload.maincompanyid = selectedMain; await handleSaveUpdate(payload); }} />
      )}

      {modal.type === "deleteUpdate" && (
        <Modal title="Delete Update" onClose={() => setModal({ type: null })}>
          <p>Are you sure you want to delete this update/feature? This cannot be undone.</p>
          <div className="mt-4 flex justify-end gap-2">
            <button className="px-3 py-1 border rounded" onClick={() => setModal({ type: null })}>Cancel</button>
            <button className="px-3 py-1 bg-red-600 text-white rounded" onClick={() => handleDeleteUpdate(modal.data.id)}>Yes, Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ----------------- Small sub-components (kept in same file for convenience) -----------------

function PageEditModal({ data = {}, onClose, onSave, onDelete }) {
  const [form, setForm] = useState({
    id: data.id,
    page_name: data.page_name || "",
    title: data.title || "",
    value: data.value || "",
    keywords: data.keywords || "",
    meta_teg: data.meta_teg || "",
    meta_desc: data.meta_desc || "",
    intro_image: data.intro_image || "",
    background_image: data.background_image || "",
    status: data.status || "0",
    intro_file: null,
    background_file: null,
  });

  useEffect(() => {
    setForm((f) => ({ ...f, ...data }));
  }, [data]);

  return (
    <Modal title={`Edit Page — ${form.page_name || form.title || "Page"}`} size="4xl" onClose={onClose}>
      <div className="grid grid-cols-1 gap-3 text-sm">
        <label className="text-xs">Page Key (readonly)</label>
        <input type="text" value={form.page_name} readOnly className="w-full border rounded p-2 text-sm bg-gray-50" />

        <label className="text-xs">Title (optional)</label>
        <input type="text" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border rounded p-2 text-sm" />

        <label className="text-xs">Content (HTML allowed)</label>
        <Editor
          value={form.value || ""}
          onEditorChange={(newValue) =>
            setForm({ ...form, value: newValue })   // ✅ updates same state
          }
          apiKey="mpwzqoajpwgkybw09orm71pipq474ewfqapm6hde2mws0a0t"
          init={{
            height: 400,
            menubar: false,
            plugins: "code link lists table image",
            toolbar:
              "undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist outdent indent | code",
          }}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs">Intro Image</label>
            <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, intro_file: e.target.files?.[0] })} className="w-full" />
            {form.intro_image && <div className="mt-2 text-xs text-gray-600">Current: {form.intro_image}</div>}
          </div>

          <div>
            <label className="text-xs">Background Image</label>
            <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, background_file: e.target.files?.[0] })} className="w-full" />
            {form.background_image && <div className="mt-2 text-xs text-gray-600">Current: {form.background_image}</div>}
          </div>
        </div>

        <label className="text-xs">Keywords (comma separated)</label>
        <input type="text" value={form.keywords || ""} onChange={(e) => setForm({ ...form, keywords: e.target.value })} className="w-full border rounded p-2 text-sm" />

        <label className="text-xs">Meta Tag (meta_teg field)</label>
        <input type="text" value={form.meta_teg || ""} onChange={(e) => setForm({ ...form, meta_teg: e.target.value })} className="w-full border rounded p-2 text-sm" />

        <label className="text-xs">Meta Description</label>
        <textarea value={form.meta_desc || ""} onChange={(e) => setForm({ ...form, meta_desc: e.target.value })} className="w-full border rounded p-2 text-sm" rows={3} />

        <div className="flex items-center gap-2">
          <input type="checkbox" checked={form.status === "1"} onChange={(e) => setForm({ ...form, status: e.target.checked ? "1" : "0" })} />
          <label className="text-sm">Active</label>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400">Cancel</button>
          <button onClick={() => onSave && onSave(form)} className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">Update</button>
        </div>
      </div>
    </Modal>
  );
}

function FaqEditModal({ data = {}, onClose, onSave }) {
  const [form, setForm] = useState({
    id: data.id || null,
    question: data.question || "",
    answer: data.value || "", // map value -> answer
    status: data.status || "0",
  });

  // Sync form when data changes
  useEffect(() => {
    setForm({
      id: data.id || null,
      question: data.question || "",
      answer: data.value || "", // map value -> answer
      status: data.status || "0",
    });
  }, [data]);

  // Convert answer back to value on save
  const handleSave = () => {
    onSave({
      ...form,
      value: form.answer, // API expects value field
    });
  };

  return (
    <Modal title={form.id ? "Edit FAQ" : "Add FAQ"} size="4xl" onClose={onClose}>
      <div className="grid gap-3 text-sm">
        <label className="text-xs">Question</label>
        <input
          type="text"
          value={form.question}
          onChange={(e) => setForm({ ...form, question: e.target.value })}
          className="w-full border rounded p-2 text-sm"
        />

        <label className="text-xs">Answer (HTML allowed)</label>
        <Editor
          value={form.answer || ""}
          onEditorChange={(newValue) =>
            setForm({ ...form, answer: newValue })   // ✅ update state
          }
          apiKey="mpwzqoajpwgkybw09orm71pipq474ewfqapm6hde2mws0a0t"
          init={{
            height: 300,
            menubar: false,
            plugins: "code link lists table image",
            toolbar:
              "undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist outdent indent | code",
          }}
        />


        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.status === "1"}
            onChange={(e) =>
              setForm({ ...form, status: e.target.checked ? "1" : "0" })
            }
          />
          <label className="text-sm">Active</label>
        </div>

        <div className="flex justify-end gap-2 mt-3">
          <button
            className="px-3 py-1 border rounded"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-3 py-1 bg-blue-600 text-white rounded"
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
}


function UpdateEditModal({ data = {}, onClose, onSave }) {
  const [form, setForm] = useState({ id: data.id || null, title: data.question || "", summary: data.keywords || "", content: data.value || "", status: data.status || "0", image: data.image || "", file: null });
  useEffect(() => setForm({ id: data.id || null, title: data.question || "", summary: data.keywords || "", content: data.value || "", status: data.status || "0", image: data.image || "", file: null }), [data]);

  return (
    <Modal title={form.id ? "Edit Update" : "Add Update"} size="4xl" onClose={onClose}>
      <div className="grid gap-3 text-sm">
        <label className="text-xs">Title</label>
        <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border rounded p-2 text-sm" />

        <label className="text-xs">Summary</label>
        <input type="text" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="w-full border rounded p-2 text-sm" />

        <label className="text-xs">Content (HTML allowed)</label>
        <Editor
          value={form.content}
          onEditorChange={(newValue) =>
            setForm({ ...form, content: newValue })   // ✅ update form state
          }
          apiKey="mpwzqoajpwgkybw09orm71pipq474ewfqapm6hde2mws0a0t"
          init={{
            height: 400,
            menubar: false,
            plugins: "code link lists table image",
            toolbar:
              "undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist outdent indent | code",
          }}
        />

        <label className="text-xs">Image</label>
        <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] })} className="w-full" />
        {form.image && <div className="text-xs text-gray-600">Current: {form.image}</div>}

        <div className="flex items-center gap-2">
          <input type="checkbox" checked={form.status === "1"} onChange={(e) => setForm({ ...form, status: e.target.checked ? "1" : "0" })} />
          <label className="text-sm">Active</label>
        </div>

        <div className="flex justify-end gap-2 mt-3">
          <button className="px-3 py-1 border rounded" onClick={onClose}>Cancel</button>
          <button className="px-3 py-1 bg-blue-600 text-white rounded" onClick={() => onSave(form)}>Save</button>
        </div>
      </div>
    </Modal>
  );
}
